import React, { useMemo, useState } from 'react';
import { DefenderChatArtifactType, DefenderChatHistoryItem } from 'client-factories/MicrosoftConsumerSecurityCopilotClient';
import { pageDataService } from 'common';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

import { IChatContext, IChatHistoryItem } from './Chat.types';

const defaultChatContext: IChatContext = {
  history: [],
  prompt: undefined,
  temperature: 0,
  topP: 0,
  seed: '0',
  useExperimentalModel: false,
  setHistory: () => {},
  setPrompt: () => {},
  setTemperature: () => {},
  setTopP: () => {},
  setSeed: () => {},
  setExperimentalModel: () => {},
  setArtifactType: () => {},
  setArtifactId: () => {},
};

export const ChatContext = React.createContext<IChatContext>(defaultChatContext);

export interface IChatContextProviderProps {
  initialChat?: DefenderChatHistoryItem[];
  defaultPrompt?: string;
  defaultArtifactType?: DefenderChatArtifactType;
  defaultArtifactId?: string;
  children: JSX.Element;
}

export const ChatContextProvider = ({
  children,
  defaultPrompt,
  defaultArtifactType,
  defaultArtifactId,
  initialChat,
}: IChatContextProviderProps) => {
  const [history, setHistory] = useState<IChatHistoryItem[]>(() =>
    (initialChat ?? []).map((item, index) => ({ ...item, id: `${Date.now()}_${index}`, apiStatus: ApiCallResultStatus.SUCCESS }))
  );

  const copilotSettings = pageDataService.getCopilotSettings();
  const [prompt, setPrompt] = useState<string | undefined>(defaultPrompt);
  const [temperature, setTemperature] = useState<number>(copilotSettings?.temperature || 0);
  const [topP, setTopP] = useState<number>(copilotSettings?.topP || 0);
  const [seed, setSeed] = useState<string>(copilotSettings?.seed?.toString() || '1');
  const [useExperimentalModel, setExperimentalModel] = useState<boolean>(false);
  const [artifactType, setArtifactType] = useState<DefenderChatArtifactType | undefined>(defaultArtifactType);
  const [artifactId, setArtifactId] = useState<string | undefined>(defaultArtifactId);

  const contextValue = useMemo(
    () => ({
      history,
      prompt,
      temperature,
      topP,
      seed,
      useExperimentalModel,
      artifactType,
      artifactId,
      setPrompt,
      setTemperature,
      setTopP,
      setSeed,
      setHistory,
      setExperimentalModel,
      setArtifactType,
      setArtifactId,
    }),
    [artifactId, artifactType, history, prompt, seed, temperature, topP, useExperimentalModel]
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};
